import {
  BlockContainer,
  Button,
  DeliveryInformation,
  OrderSummary,
  Text,
  Title,
} from '@components';

import { useLazyPlaceOrderQuery } from '@api/api';
import { DealerAddress } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { useCartContext } from '@context/CartContext';
import { useAppSelector, useInternalRedirect, useToast } from '@hooks';
import { useCart } from '@hooks/cart/cart.hooks';
import { Box, Grid, styled } from '@mui/material';
import { getSpecialPage, getSystemPages } from '@store/app/app.slice';
import { getDealer } from '@store/dealer/dealer.slice';
import { t } from '@translations';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

const CheckoutBlockWrapper = styled(Box)``;

type CheckoutBlockProps = {
  children: React.ReactNode;
  title?: string;
};

export type CheckoutForm = {
  deliveryAddressId: string;
  deliveryNote: string;
  orderReference: string;
};

export const CheckoutBlock = ({ children, title }: CheckoutBlockProps) => {
  const { itemCountLabel, clearCart } = useCart();
  const { cartSubTotal, cartTaxTotal, cartTotal } = useCartContext();
  const [trigger, response] = useLazyPlaceOrderQuery();
  const { isFetching, isLoading } = response;
  const isWorking = isFetching || isLoading;
  const { toast } = useToast();
  const dealer = useSelector(getDealer);
  const address = dealer.address?.filter((a: DealerAddress) => a.addressType === 'Public');
  const disableOrderMessage = useSelector(getSystemPages).disableOrderMessage as string;
  const disableOrderProcessing = useSelector(getSystemPages).disableOrderProcessing as boolean;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CheckoutForm>();
  const redirect = useInternalRedirect();
  const checkoutPage = useAppSelector(getSpecialPage('confirmationPage'));

  const onSubmit = async (formData: CheckoutForm) => {
    await trigger(formData)
      .unwrap()
      .then((res) => {
        if (res) {
          clearCart();
          redirect(`${checkoutPage}${res?.data?.orderNo}`);
        }
      })
      .catch((err) => {
        console.error(err);
        toast({
          severity: 'error',
          message: t(TextCodes.Product.Checkout.OrderError),
        });
      });
  };
  const buttons = (
    <>
      <Button
        busy={isWorking}
        variant='secondary'
        fullWidth
        disabled={address?.length === 0 || disableOrderProcessing}
        type='submit'
      >
        {t(TextCodes.Product.Checkout.PlaceOrder)}
      </Button>
    </>
  );
  return (
    <BlockContainer>
      <CheckoutBlockWrapper className='checkout-block'>
        <Title>{title || t(TextCodes.Product.Checkout.Checkout)}</Title>
        <Box sx={{ mt: 8 }} component='form' onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7} lg={8}>
              <Text variant='h3' sx={{ mb: 4 }}>
                {t(TextCodes.Product.Checkout.DeliveryInfo)}
              </Text>
              <DeliveryInformation control={control} errors={errors} address={address} />
            </Grid>
            <Grid item xs={12} md={5} lg={4}>
              <OrderSummary
                title={t(TextCodes.Product.Checkout.OrderSummary)}
                itemCount={itemCountLabel}
                subtotal={cartSubTotal}
                tax={cartTaxTotal}
                total={cartTotal}
                buttons={buttons}
                additionalMessage= {disableOrderProcessing ? disableOrderMessage : ''}
              />
            </Grid>
          </Grid>
        </Box>
      </CheckoutBlockWrapper>
      {children}
    </BlockContainer>
  );
};
